import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomToggleSwitch from "../CustomToggleSwitch";
import { selectAccountData } from "../../redux/main/accountSlice";
import DynamicIcon from "../DynamicIcon";
import LoadingSection from "../LoadingSection";

export default function AddCustomerModal({ onClose, onCustomerAdded }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const [isLoading, setIsLoading] = useState(false);
    const [isProspect, setIsProspect] = useState(false);
    const token = localStorage.getItem('installamelo-auth-token');
    
    // Stato per i dati del form
    const [customerData, setCustomerData] = useState({
        accountId: accountData?._id || '',
        name: '',
        surname: '',
        company: '',
        business: false,
        vat: '',
        fiscalCode: '',
        sdiCode: '',
        notes: '',
        prospect: false,
        // Contatto predefinito
        contactsList: [{
            name: "",
            surname: "",
            role: "",
            phone: "",
            mobile: "",
            email: "",
            contactTypeLabel: ""
        }]
    });

    // Aggiorna accountId quando accountData cambia
    useEffect(() => {
        if (accountData?._id) {
            setCustomerData(prevState => ({
                ...prevState,
                accountId: accountData._id,
                prospect: isProspect
            }));
        }
    }, [accountData, isProspect]);

    // Gestisce il cambio del tipo (cliente/prospect)
    const handleTypeChange = (isChecked) => {
        setIsProspect(isChecked);
        setCustomerData(prevState => ({
            ...prevState,
            prospect: isChecked
        }));
    };

    // Aggiorna i dati del form
    const handleChange = (e) => {
        const { id, value, type, checked } = e.target;
        setCustomerData(prevState => ({
            ...prevState,
            [id]: type === 'checkbox' ? checked : value
        }));
    };

    // Aggiorna il flag business
    const handleBusinessChange = (isChecked) => {
        setCustomerData(prevState => ({
            ...prevState,
            business: isChecked
        }));
    };

    // Gestione dei dati di contatto
    const handleContactChange = (e, index = 0) => {
        const { id, value } = e.target;
        const field = id.split('-')[1]; // es. contact-email => email
        
        setCustomerData(prevState => {
            const updatedContacts = [...prevState.contactsList];
            updatedContacts[index] = {
                ...updatedContacts[index],
                [field]: value
            };
            
            return {
                ...prevState,
                contactsList: updatedContacts
            };
        });
    };

    // Salva il cliente/prospect
    const handleSave = async () => {
        // Validazioni di base
        if (!customerData.accountId) {
            toast.error("ID account mancante");
            return;
        }

        if (customerData.business && !customerData.company) {
            toast.error("Inserisci il nome dell'azienda");
            return;
        }

        if (!customerData.business && (!customerData.name || !customerData.surname)) {
            toast.error("Inserisci nome e cognome");
            return;
        }

        // Salva il nuovo cliente/prospect
        setIsLoading(true);
        try {
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/customers`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify(customerData)
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Errore durante il salvataggio');
            }
            
            const data = await response.json();
            
            if (data && data.data) {
                toast.success(`${isProspect ? 'Prospect' : 'Cliente'} aggiunto con successo!`);
                // Notifica il componente padre che un cliente è stato aggiunto
                if (onCustomerAdded) {
                    onCustomerAdded(data.data);
                }
            } else {
                throw new Error('Risposta del server non valida');
            }
        } catch (error) {
            console.error('Errore durante il salvataggio:', error);
            toast.error(`Errore: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                {isLoading && <LoadingSection isFixed />}
                
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-semibold">
                            {isProspect ? 'Aggiungi Nuovo Prospect' : 'Aggiungi Nuovo Cliente'}
                        </h2>
                        <button 
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <DynamicIcon iconClass="fa-solid fa-times" />
                        </button>
                    </div>

                    <div className="mb-6 flex items-center">
                        <span className="mr-2">Cliente</span>
                        <CustomToggleSwitch 
                            isChecked={isProspect}
                            onChange={handleTypeChange}
                        />
                        <span className="ml-2">Prospect</span>
                    </div>

                    <div className="space-y-4">
                        {/* Business switch */}
                        <div className="flex items-center mb-4">
                            <span className="mr-2">Privato</span>
                            <CustomToggleSwitch 
                                isChecked={customerData.business}
                                onChange={handleBusinessChange}
                            />
                            <span className="ml-2">Azienda</span>
                        </div>

                        {/* Campi diversi in base al tipo (azienda/privato) */}
                        {customerData.business ? (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nome Azienda*
                                </label>
                                <input
                                    type="text"
                                    id="company"
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={customerData.company}
                                    onChange={handleChange}
                                />
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nome*
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        className="w-full p-2 border border-gray-300 rounded-md"
                                        value={customerData.name}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Cognome*
                                    </label>
                                    <input
                                        type="text"
                                        id="surname"
                                        className="w-full p-2 border border-gray-300 rounded-md"
                                        value={customerData.surname}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Dati fiscali */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {customerData.business ? 'Partita IVA' : 'Codice Fiscale'}
                                </label>
                                <input
                                    type="text"
                                    id={customerData.business ? 'vat' : 'fiscalCode'}
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                    value={customerData.business ? customerData.vat : customerData.fiscalCode}
                                    onChange={handleChange}
                                />
                            </div>
                            {customerData.business && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Codice SDI
                                    </label>
                                    <input
                                        type="text"
                                        id="sdiCode"
                                        className="w-full p-2 border border-gray-300 rounded-md"
                                        value={customerData.sdiCode}
                                        onChange={handleChange}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Contatto principale */}
                        <div className="mt-4">
                            <h3 className="font-medium mb-2">Contatto Principale</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        id="contact-email"
                                        className="w-full p-2 border border-gray-300 rounded-md"
                                        value={customerData.contactsList[0]?.email || ''}
                                        onChange={(e) => handleContactChange(e)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Telefono
                                    </label>
                                    <input
                                        type="text"
                                        id="contact-phone"
                                        className="w-full p-2 border border-gray-300 rounded-md"
                                        value={customerData.contactsList[0]?.phone || ''}
                                        onChange={(e) => handleContactChange(e)}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Cellulare
                                    </label>
                                    <input
                                        type="text"
                                        id="contact-mobile"
                                        className="w-full p-2 border border-gray-300 rounded-md"
                                        value={customerData.contactsList[0]?.mobile || ''}
                                        onChange={(e) => handleContactChange(e)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ruolo
                                    </label>
                                    <input
                                        type="text"
                                        id="contact-role"
                                        className="w-full p-2 border border-gray-300 rounded-md"
                                        value={customerData.contactsList[0]?.role || ''}
                                        onChange={(e) => handleContactChange(e)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Note */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Note
                            </label>
                            <textarea
                                id="notes"
                                className="w-full p-2 border border-gray-300 rounded-md"
                                rows="3"
                                value={customerData.notes}
                                onChange={handleChange}
                            ></textarea>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            onClick={onClose}
                        >
                            Annulla
                        </button>
                        <button
                            type="button"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            onClick={handleSave}
                            disabled={isLoading}
                        >
                            Salva
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
